<template>
  <div class="legal-page">
    <header class="legal-page__nav">
      <div class="legal-page__container legal-page__nav-inner">
        <router-link to="/" class="legal-page__brand">
          <img src="/logo-banner.png" alt="SocialLabs" class="legal-page__logo" />
        </router-link>
        <div class="legal-page__nav-actions">
          <LocaleSwitcher />
          <router-link to="/" class="btn btn-sm btn-outline-primary">{{ $t('legal.back_home') }}</router-link>
        </div>
      </div>
    </header>

    <main class="legal-page__main">
      <div class="legal-page__container">
        <article v-if="doc" class="legal-doc">
          <h1 class="legal-doc__title">{{ doc.title }}</h1>
          <p class="legal-doc__updated">{{ $t('legal.last_updated', { date: doc.updated }) }}</p>

          <section v-for="(section, idx) in doc.sections" :key="idx" class="legal-doc__section">
            <h2>{{ section.heading }}</h2>
            <p v-for="(para, pIdx) in section.paragraphs" :key="pIdx">{{ para }}</p>
          </section>
        </article>

        <aside class="legal-doc__aside">
          <h6>{{ $t('legal.related') }}</h6>
          <router-link
            v-for="link in relatedLinks"
            :key="link.to"
            :to="link.to"
            class="legal-doc__link"
            :class="{ 'is-active': link.to === $route.path }"
          >
            {{ link.label }}
          </router-link>
        </aside>
      </div>
    </main>

    <footer class="legal-page__footer">
      <div class="legal-page__container d-flex flex-wrap justify-content-between align-items-center">
        <span class="text-muted small">© {{ year }} SocialLabs</span>
        <div class="legal-page__footer-links">
          <router-link v-for="link in footerLinks" :key="link.to" :to="link.to">{{ link.label }}</router-link>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import { getLegalPage } from '@/content/legal'

export default {
  components: { LocaleSwitcher },
  props: {
    page: {
      type: String,
      required: true,
    },
  },
  computed: {
    doc() {
      return getLegalPage(this.page, this.$i18n.locale)
    },
    year() {
      return new Date().getFullYear()
    },
    relatedLinks() {
      return [
        { to: '/terms', label: this.$t('legal.terms') },
        { to: '/privacy', label: this.$t('legal.privacy') },
        { to: '/cookies', label: this.$t('legal.cookies') },
        { to: '/refund', label: this.$t('legal.refund') },
      ]
    },
    footerLinks() {
      return this.relatedLinks
    },
  },
  watch: {
    doc: {
      immediate: true,
      handler(doc) {
        if (doc?.title) {
          document.title = `${doc.title} - SocialLabs`
        }
      },
    },
    '$i18n.locale'() {
      if (this.doc?.title) {
        document.title = `${this.doc.title} - SocialLabs`
      }
    },
  },
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: var(--wc-bg-page, #f4f6f9);
  color: var(--wc-text, #3e5569);
  display: flex;
  flex-direction: column;
}

.legal-page__container {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.25rem;
}

.legal-page__nav {
  background: #fff;
  border-bottom: 1px solid var(--wc-border, #e3e6ef);
  position: sticky;
  top: 0;
  z-index: 20;
}

.legal-page__nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 68px;
}

.legal-page__brand {
  display: inline-flex;
  align-items: center;
}

.legal-page__logo {
  height: 36px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  object-position: left center;
  display: block;
}

.legal-page__nav-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.75rem;
}

.legal-page__main {
  flex: 1;
  padding: 2.5rem 0 3rem;
}

.legal-page__main > .legal-page__container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 2rem;
  align-items: start;
}

.legal-doc {
  background: #fff;
  border: 1px solid var(--wc-border, #e3e6ef);
  border-radius: 12px;
  padding: 2rem 2.25rem;
  box-shadow: var(--wc-shadow-sm);
}

.legal-doc__title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--wc-text-heading, #272b41);
  margin-bottom: 0.35rem;
}

.legal-doc__updated {
  color: var(--wc-text-muted, #6c757d);
  font-size: 0.9rem;
  margin-bottom: 1.75rem;
}

.legal-doc__section {
  margin-bottom: 1.5rem;
}

.legal-doc__section h2 {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--wc-text-heading, #272b41);
  margin-bottom: 0.65rem;
}

.legal-doc__section p {
  margin-bottom: 0.65rem;
  line-height: 1.65;
  font-size: 0.95rem;
}

.legal-doc__aside {
  position: sticky;
  top: 88px;
  background: #fff;
  border: 1px solid var(--wc-border, #e3e6ef);
  border-radius: 12px;
  padding: 1.25rem;
}

.legal-doc__aside h6 {
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--wc-text-heading, #272b41);
}

.legal-doc__link {
  display: block;
  padding: 0.4rem 0;
  color: var(--wc-text, #3e5569);
  font-size: 0.9rem;
  font-weight: 500;
}

.legal-doc__link.is-active,
.legal-doc__link:hover {
  color: var(--wc-primary, #2976fb);
  text-decoration: none;
}

.legal-page__footer {
  background: #fff;
  border-top: 1px solid var(--wc-border, #e3e6ef);
  padding: 1rem 0;
}

.legal-page__footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.legal-page__footer-links a {
  color: var(--wc-text, #3e5569);
  font-size: 0.85rem;
  font-weight: 500;
}

.legal-page__footer-links a:hover {
  color: var(--wc-primary, #2976fb);
  text-decoration: none;
}

@media (max-width: 991px) {
  .legal-page__main > .legal-page__container {
    grid-template-columns: 1fr;
  }

  .legal-doc__aside {
    position: static;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.25rem;
  }

  .legal-doc__aside h6 {
    width: 100%;
  }

  .legal-doc {
    padding: 1.5rem 1.25rem;
  }
}
</style>
